/**
 * The action bar.
 *
 * Three decisions, three colours, always in the same place: Fold red, the
 * passive option green, the aggressive one amber. A player under a twenty
 * second clock is not reading — they are hitting the shape they already know,
 * and moving a button between hands is how a misclick becomes a lost stack.
 *
 * Sizing sits above them, because sizing is a second decision and mixing it
 * into the same row makes both slower. Preflop the shortcuts are multiples of
 * the big blind, which is how an open is actually described; postflop they are
 * fractions of the pot, which is how a bet is.
 *
 * Every value is clamped to the bounds the server sent. The client never
 * decides what is legal — it only picks a number inside what it was given, and
 * the server rejecting an action is the actual rule.
 */

import { useEffect, useState } from 'react';

import type { PlayerAction, TableSnapshot } from '@pf/protocol';

import { bb, bbValue } from '../fa.ts';

export function Actions({
  snapshot,
  onAct,
  onStand,
  onSittingOut,
  onRebuy,
}: {
  snapshot: TableSnapshot;
  onAct: (action: PlayerAction) => void;
  onStand: () => void;
  onSittingOut: (out: boolean) => void;
  onRebuy: (amount: number) => void;
}) {
  const legal = snapshot.legalActions;
  const sizing = legal?.bet ?? legal?.raise ?? null;
  const [to, setTo] = useState(0);
  const big = snapshot.bigBlind;

  useEffect(() => {
    if (sizing !== null) setTo(sizing.min);
  }, [sizing?.min, sizing?.max]);

  if (snapshot.yourSeat === null) {
    // A spectator had no way out. Standing up from a table leaves you watching
    // it, and the only control on the screen at that point said "sit down" —
    // so somebody who wanted to go and look at the tournaments was stuck at a
    // table they had already left, with the browser's back button as the only
    // answer the room had for them.
    return (
      <div className="actions">
        <div className="row quiet">
          <span>تماشاگر هستید — روی یک صندلی خالی بنشینید.</span>
          <button className="btn-plain" onClick={onStand}>
            Back to Lobby
          </button>
        </div>
      </div>
    );
  }

  const you = snapshot.seats.find((s) => s.seat === snapshot.yourSeat);
  // In a tournament the chips are not yours to take and cannot be topped up,
  // so "Leave Table" means leaving the screen, not the game — your stack stays
  // and keeps posting blinds until somebody wins it.
  const inTournament = snapshot.tournament !== null;
  const leaveLabel = inTournament ? 'Back to Lobby' : 'Leave Table';
  const sittingOut = you?.status === 'sitting-out';
  const leavingAfterThisHand = you?.sitOutNextHand === true && !sittingOut;

  if (you !== undefined && you.stack === 0 && !inTournament) {
    return (
      <div className="actions">
        <div className="notice">
          <span>چیپی برایتان نمانده.</span>
          <button className="btn-raise" onClick={() => onRebuy(snapshot.maxBuyIn)}>
            Rebuy {bb(snapshot.maxBuyIn, big)}
          </button>
          <button className="btn-plain" onClick={() => onRebuy(snapshot.minBuyIn)}>
            {bb(snapshot.minBuyIn, big)}
          </button>
          <button className="btn-plain danger-text" onClick={onStand}>
            {leaveLabel}
          </button>
        </div>
      </div>
    );
  }

  if (sittingOut) {
    return (
      <div className="actions">
        <div className="notice">
          {/* A tournament deals its absentees in — sitting out does not excuse
              you from the blinds — so the two games need different words for
              the same button. */}
          <span>
            {inTournament
              ? 'غایب هستید — دست‌ها خودکار فولد می‌شوند و بلایندهایتان می‌آید.'
              : 'غایب هستید — کارتی به شما داده نمی‌شود.'}
          </span>
          <button className="btn-check" onClick={() => onSittingOut(false)}>
            Sit In
          </button>
          <button className="btn-plain danger-text" onClick={onStand}>
            {leaveLabel}
          </button>
        </div>
      </div>
    );
  }

  const tableControls = (
    <div className="row controls">
      {leavingAfterThisHand ? (
        <>
          <span>از دست بعد غایب می‌شوید.</span>
          <button className="btn-plain" onClick={() => onSittingOut(false)}>
            Cancel
          </button>
        </>
      ) : (
        <button className="btn-plain" onClick={() => onSittingOut(true)}>
          Sit Out
        </button>
      )}
      <button className="btn-plain danger-text" onClick={onStand}>
        {leaveLabel}
      </button>
    </div>
  );

  if (legal === null) {
    const waitingOn = snapshot.actionOn;
    return (
      <div className="actions">
        <div className="row quiet">
          {waitingOn !== null
            ? `نوبت ${snapshot.seats.find((s) => s.seat === waitingOn)?.name ?? '…'}`
            : snapshot.waitingFor === 'paused'
              ? // Said plainly. A table that has stopped with no explanation reads
                // as broken, and the honest reason here is reassuring: the room
                // came back and is holding the game until everyone else does too.
                'بازی موقتاً نگه داشته شده تا بقیه دوباره وصل شوند — چیزی از دست نرفته.'
              : snapshot.waitingFor === 'players'
                ? 'در انتظار بازیکن دیگر — برای شروع دست دست‌کم دو نفر لازم است.'
                : 'دست بعدی به‌زودی…'}
        </div>
        {tableControls}
      </div>
    );
  }

  const clamp = (value: number): number =>
    sizing === null ? value : Math.max(sizing.min, Math.min(sizing.max, Math.round(value)));

  const pot = snapshot.potTotal;
  const preflop = snapshot.street === 'preflop';
  const isBet = legal.bet !== null;

  const shortcuts: { label: string; value: number }[] = preflop
    ? [2, 2.5, 3, 4].map((x) => ({ label: `${x}X`, value: Math.round(big * x) }))
    : [
        { label: '1/2', value: Math.round(pot / 2) },
        { label: '2/3', value: Math.round((pot * 2) / 3) },
        { label: 'Pot', value: pot },
      ];

  const sizeAction: PlayerAction | null =
    sizing === null ? null : isBet ? { type: 'bet', to } : { type: 'raise', to };
  const allIn = sizing !== null && to === sizing.max;
  const step = Math.max(1, big);

  return (
    <div className="actions">
      {sizing !== null && sizing.max > sizing.min && (
        <div className="sizing">
          <div className="shortcuts">
            {shortcuts.map((s) => (
              <button
                key={s.label}
                className={`chip-btn${to === clamp(s.value) ? ' on' : ''}`}
                onClick={() => setTo(clamp(s.value))}
              >
                {s.label}
              </button>
            ))}
            <button className={`chip-btn${allIn ? ' on' : ''}`} onClick={() => setTo(sizing.max)}>
              Max
            </button>
          </div>

          <div className="stepper">
            <button
              className="step"
              aria-label="کمتر"
              disabled={to <= sizing.min}
              onClick={() => setTo(clamp(to - step))}
            >
              −
            </button>
            <span className="amount">{bbValue(to, big)}</span>
            <span className="unit">BB</span>
            <button
              className="step"
              aria-label="بیشتر"
              disabled={to >= sizing.max}
              onClick={() => setTo(clamp(to + step))}
            >
              +
            </button>
          </div>

          <input
            type="range"
            min={sizing.min}
            max={sizing.max}
            step={1}
            value={to}
            onChange={(event) => setTo(Number(event.target.value))}
            aria-label="اندازه‌ی شرط"
          />
        </div>
      )}

      <div className="row primary-row">
        <button className="btn-fold" onClick={() => onAct({ type: 'fold' })}>
          Fold
        </button>

        {legal.check && (
          <button className="btn-check" onClick={() => onAct({ type: 'check' })}>
            Check
          </button>
        )}

        {legal.call !== null && (
          <button className="btn-check" onClick={() => onAct({ type: 'call' })}>
            <span className="lead">{legal.call.allIn ? 'All In' : 'Call'}</span>
            <span className="sub">{bb(legal.call.amount, big)}</span>
          </button>
        )}

        {sizeAction !== null && (
          <button className="btn-raise" onClick={() => onAct(sizeAction)}>
            <span className="lead">{allIn ? 'All In' : isBet ? 'Bet' : 'Raise'}</span>
            <span className="sub">{bb(to, big)}</span>
          </button>
        )}
      </div>

      {tableControls}
    </div>
  );
}
